import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getToolIcon, getToolStepKey, getToolUnavailableKey, isToolOutputError } from './toolDisplay.tsx';
import { ToolResultCard } from './ToolResultCard.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import classnames from '../../core/utils/classnames.tsx';

const ChevronIcon = withIcon(faChevronDown);

export const ToolResultsAccordion = ({
    toolCalls,
    messageId = null,
    messageToolCalls = [],
    referencedEmailIds = null,
    conversationAttachments = [],
    onAction = null,
    onPreviewAttachment = null,
    disabled = false,
}: {
    toolCalls: any[];
    messageId?: string | null;
    messageToolCalls?: any[];
    referencedEmailIds?: string[] | null;
    conversationAttachments?: any[];
    onAction?: ((...args: any[]) => void) | null;
    onPreviewAttachment?: ((...args: any[]) => void) | null;
    disabled?: boolean;
}) => {
    const { t } = useTranslation('chat');
    const [groupExpanded, setGroupExpanded] = useState(false);
    const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set());

    if (!toolCalls.length) return null;

    const toggleRow = (idx: number) => {
        setOpenIndexes((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    return (
        <div className="mb-2">
            <button
                type="button"
                onClick={() => setGroupExpanded(!groupExpanded)}
                className="flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-xs font-medium text-content-weak"
            >
                <ChevronIcon
                    className={classnames('transition-transform', groupExpanded ? 'rotate-180' : '-rotate-90')}
                />
                {t('thread.results', { count: toolCalls.length })}
            </button>
            {groupExpanded && (
                <div className="ml-1.25 mt-1.5 flex flex-col gap-1 border-0 border-l border-solid border-line-weak pl-3">
                    {toolCalls.map((toolCall, idx) => {
                        const toolName = toolCall.tool_name || '';
                        const StepIcon = getToolIcon(toolName);
                        const failed = isToolOutputError(toolCall.tool_output);
                        const label = t(failed ? getToolUnavailableKey(toolName) : getToolStepKey(toolName));
                        const rowOpen = openIndexes.has(idx);
                        return (
                            <div key={idx}>
                                <button
                                    type="button"
                                    onClick={() => toggleRow(idx)}
                                    className="flex w-full cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-xs text-content-weak"
                                >
                                    <StepIcon className="text-content-brand-strong" />
                                    <span className="flex-1 text-left">{label}</span>
                                    <ChevronIcon
                                        className={classnames('transition-transform', rowOpen ? 'rotate-180' : '')}
                                    />
                                </button>
                                {rowOpen && (
                                    <ToolResultCard
                                        toolCall={toolCall}
                                        messageId={messageId}
                                        toolCallIndex={messageToolCalls.indexOf(toolCall)}
                                        referencedEmailIds={referencedEmailIds}
                                        conversationAttachments={conversationAttachments}
                                        onAction={onAction}
                                        onPreviewAttachment={onPreviewAttachment}
                                        disabled={disabled}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
